import { ApiError } from "./client";

/** Пауза перед повторной попыткой, мс. Первый повтор почти сразу, второй — заметно позже. */
const RETRY_DELAYS_MS = [300, 900];

/**
 * Сбой, после которого имеет смысл повторить тот же запрос.
 *
 * Это ровно два случая: сервер сказал «слишком часто» (429) или «мне сейчас плохо»
 * (5xx), и запрос не дошёл вовсе (обрыв связи — тогда до нас летит не `ApiError`).
 * Отказ по существу — «нет такого», «нельзя», «неверный запрос» — повторять
 * бессмысленно: ответ будет тем же.
 */
export function isTransientFailure(error: unknown): boolean {
    if (error instanceof ApiError) {
        return error.status === 429 || error.status >= 500;
    }
    // Обрыв связи `fetch` бросает именно `TypeError` — только он и повторяется.
    // Раньше повторялось ВСЁ, что не `ApiError`: ошибка разбора JSON, `AbortError`
    // отменённого запроса и любой наш собственный баг превращались в три запроса
    // и лишнюю секунду ожидания вместо немедленного отказа.
    return error instanceof TypeError;
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Выполнить запрос, повторив его при временном сбое.
 *
 * Нужен там, где отсутствие ответа неотличимо от отсутствия данных: списки
 * подставляют рядом со строкой имя автора, и пустое имя читается как «запись
 * потеряна», хотя сервер всего лишь моргнул. Две повторные попытки закрывают
 * именно этот случай; отказ по существу пробрасывается сразу, без задержек.
 */
export async function retryTransient<T>(request: () => Promise<T>): Promise<T> {
    let lastError: unknown;
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
        try {
            return await request();
        } catch (error) {
            lastError = error;
            const delay = RETRY_DELAYS_MS[attempt];
            if (delay === undefined || !isTransientFailure(error)) break;
            await sleep(delay);
        }
    }
    throw lastError;
}
